
import { useState } from 'react';

const QuickHelpSection = () => {
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [helpFormData, setHelpFormData] = useState({
    name: '',
    phone: '',
    email: '',
    helpType: '',
    urgency: '',
    location: '',
    description: ''
  });
  const [servicesFormData, setServicesFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    experience: '',
    availability: '',
    location: '',
    description: '',
    price: ''
  });
  const [helpSubmitStatus, setHelpSubmitStatus] = useState('');
  const [servicesSubmitStatus, setServicesSubmitStatus] = useState('');
  const [isHelpSubmitting, setIsHelpSubmitting] = useState(false);
  const [isServicesSubmitting, setIsServicesSubmitting] = useState(false);

  const helpTypes = [
    'Aide administrative',
    'Transport',
    'Bricolage/Réparations',
    'Garde d\'enfants',
    'Courses/Livraisons',
    'Traduction',
    'Informatique',
    'Autre'
  ];

  const serviceTypes = [
    'Aide administrative',
    'Transport/Livraison',
    'Bricolage/Réparations',
    'Garde d\'enfants',
    'Ménage/Nettoyage',
    'Cours particuliers',
    'Traduction',
    'Informatique',
    'Cuisine/Traiteur',
    'Autre'
  ];

  const urgencyLevels = [
    'Urgent (dans la journée)',
    'Rapide (dans la semaine)',
    'Normal (dans le mois)',
    'Flexible'
  ];

  const experienceLevels = [
    'Débutant (moins d\'1 an)',
    'Intermédiaire (1-3 ans)',
    'Expérimenté (3-5 ans)',
    'Expert (plus de 5 ans)'
  ];

  const availabilityOptions = [
    'Temps plein',
    'Temps partiel',
    'Week-ends uniquement',
    'Soirées uniquement',
    'Ponctuel/Occasionnel'
  ];

  const handleHelpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsHelpSubmitting(true);
    setHelpSubmitStatus('');

    try {
      const formData = new URLSearchParams();
      Object.entries(helpFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d45qmmuj3hkp5gme3sc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        setHelpSubmitStatus('success');
        setHelpFormData({
          name: '',
          phone: '',
          email: '',
          helpType: '',
          urgency: '',
          location: '',
          description: ''
        });
        setTimeout(() => {
          setShowHelpModal(false);
          setHelpSubmitStatus('');
        }, 2000);
      } else {
        setHelpSubmitStatus('error');
      }
    } catch (error) {
      setHelpSubmitStatus('error');
    } finally {
      setIsHelpSubmitting(false);
    }
  };

  const handleServicesSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsServicesSubmitting(true);
    setServicesSubmitStatus('');

    try {
      const formData = new URLSearchParams();
      Object.entries(servicesFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d45qmmuj3hkp5gme3sc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        setServicesSubmitStatus('success');
        setServicesFormData({
          name: '',
          phone: '',
          email: '',
          serviceType: '',
          experience: '',
          availability: '',
          location: '',
          description: '',
          price: ''
        });
        setTimeout(() => {
          setShowServicesModal(false);
          setServicesSubmitStatus('');
        }, 2000);
      } else {
        setServicesSubmitStatus('error');
      }
    } catch (error) {
      setServicesSubmitStatus('error');
    } finally {
      setIsServicesSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Entraide Communautaire
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ensemble, nous sommes plus forts. Demandez de l'aide ou proposez vos services à la communauté Wald Bladi.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hand-heart-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Besoin d'aide ?</h3>
              <p className="text-gray-600">
                Notre communauté est là pour vous accompagner dans vos démarches et difficultés du quotidien.
              </p>
            </div>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3"></i>
                Aide administrative et traduction
              </li>
              <li className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3"></i>
                Transport et accompagnement
              </li>
              <li className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3"></i>
                Garde d'enfants ponctuelle
              </li>
              <li className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3"></i>
                Bricolage et réparations
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Proposer ses services</h3>
              <p className="text-gray-600">
                Partagez vos compétences et aidez les membres de notre communauté tout en générant des revenus.
              </p>
            </div>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3"></i>
                Valorisez vos compétences
              </li>
              <li className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3"></i>
                Revenus complémentaires
              </li>
              <li className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3"></i>
                Réseau communautaire solide
              </li>
              <li className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3"></i>
                Flexibilité des horaires
              </li>
            </ul>
          </div>
        </div>

        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button 
              onClick={() => setShowHelpModal(true)}
              className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-hand-heart-line mr-2"></i>
              Demander de l'aide
            </button>
            <button 
              onClick={() => setShowServicesModal(true)}
              className="bg-orange-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-orange-900 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-user-heart-line mr-2"></i>
              Proposer mes services
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            <i className="ri-shield-check-line mr-1"></i>
            Toutes les demandes sont vérifiées par notre équipe
          </p>
        </div>
      </div>

      {/* Modal Demande d'aide */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-800">Demander de l'aide</h3>
                <button 
                  onClick={() => setShowHelpModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                Décrivez votre besoin et notre communauté vous aidera rapidement.
              </p>
            </div>

            <form onSubmit={handleHelpSubmit} className="p-6 space-y-6" data-readdy-form>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-user-line mr-1"></i>Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={helpFormData.name}
                    onChange={(e) => setHelpFormData({...helpFormData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-phone-line mr-1"></i>Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={helpFormData.phone}
                    onChange={(e) => setHelpFormData({...helpFormData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-mail-line mr-1"></i>Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={helpFormData.email}
                  onChange={(e) => setHelpFormData({...helpFormData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-service-line mr-1"></i>Type d'aide *
                  </label>
                  <select
                    name="helpType"
                    value={helpFormData.helpType}
                    onChange={(e) => setHelpFormData({...helpFormData, helpType: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                    required
                  >
                    <option value="">Sélectionnez...</option>
                    {helpTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-time-line mr-1"></i>Urgence *
                  </label>
                  <select
                    name="urgency"
                    value={helpFormData.urgency}
                    onChange={(e) => setHelpFormData({...helpFormData, urgency: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                    required
                  >
                    <option value="">Sélectionnez...</option>
                    {urgencyLevels.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-map-pin-line mr-1"></i>Localisation (quartier ou adresse) *
                </label>
                <input
                  type="text"
                  name="location"
                  value={helpFormData.location}
                  onChange={(e) => setHelpFormData({...helpFormData, location: e.target.value})}
                  placeholder="Ex: Centre-ville Rouen, Quartier Saint-Sever..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-file-text-line mr-1"></i>Description détaillée *
                </label>
                <textarea
                  name="description"
                  value={helpFormData.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setHelpFormData({...helpFormData, description: e.target.value});
                    }
                  }}
                  placeholder="Décrivez précisément votre besoin, les horaires souhaités, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm h-32 resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {helpFormData.description.length}/500 caractères
                </p>
              </div>

              {helpSubmitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center text-green-800">
                    <i className="ri-check-circle-line mr-2"></i>
                    Votre demande a été envoyée avec succès ! Notre équipe vous contactera rapidement.
                  </div>
                </div>
              )}

              {helpSubmitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center text-red-800">
                    <i className="ri-error-warning-line mr-2"></i>
                    Une erreur s'est produite. Veuillez réessayer.
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowHelpModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isHelpSubmitting}
                  className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isHelpSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Envoi...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Envoyer ma demande
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Proposer services */}
      {showServicesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-800">Proposer mes services</h3>
                <button 
                  onClick={() => setShowServicesModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                Partagez vos compétences avec la communauté et générez des revenus.
              </p>
            </div>

            <form onSubmit={handleServicesSubmit} className="p-6 space-y-6" data-readdy-form>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-user-line mr-1"></i>Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={servicesFormData.name}
                    onChange={(e) => setServicesFormData({...servicesFormData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-phone-line mr-1"></i>Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={servicesFormData.phone}
                    onChange={(e) => setServicesFormData({...servicesFormData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-mail-line mr-1"></i>Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={servicesFormData.email}
                  onChange={(e) => setServicesFormData({...servicesFormData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-service-line mr-1"></i>Type de service *
                  </label>
                  <select
                    name="serviceType"
                    value={servicesFormData.serviceType}
                    onChange={(e) => setServicesFormData({...servicesFormData, serviceType: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                    required
                  >
                    <option value="">Sélectionnez...</option>
                    {serviceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-star-line mr-1"></i>Niveau d'expérience *
                  </label>
                  <select
                    name="experience"
                    value={servicesFormData.experience}
                    onChange={(e) => setServicesFormData({...servicesFormData, experience: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                    required
                  >
                    <option value="">Sélectionnez...</option>
                    {experienceLevels.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-calendar-line mr-1"></i>Disponibilité *
                  </label>
                  <select
                    name="availability"
                    value={servicesFormData.availability}
                    onChange={(e) => setServicesFormData({...servicesFormData, availability: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                    required
                  >
                    <option value="">Sélectionnez...</option>
                    {availabilityOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-money-euro-circle-line mr-1"></i>Tarif indicatif
                  </label>
                  <input
                    type="text"
                    name="price"
                    value={servicesFormData.price}
                    onChange={(e) => setServicesFormData({...servicesFormData, price: e.target.value})}
                    placeholder="Ex: 15€/h, 50€/intervention..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-map-pin-line mr-1"></i>Zone d'intervention *
                </label>
                <input
                  type="text"
                  name="location"
                  value={servicesFormData.location}
                  onChange={(e) => setServicesFormData({...servicesFormData, location: e.target.value})}
                  placeholder="Ex: Rouen et agglomération, Riode..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-file-text-line mr-1"></i>Description de vos services
                </label>
                <textarea
                  name="description"
                  value={servicesFormData.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setServicesFormData({...servicesFormData, description: e.target.value});
                    }
                  }}
                  placeholder="Décrivez vos compétences..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm h-32 resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {servicesFormData.description.length}/500 caractères
                </p>
              </div>

              {servicesSubmitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center text-green-800">
                    <i className="ri-check-circle-line mr-2"></i>
                    Votre offre de service a été envoyée avec succès ! Nous vous contacterons rapidement.
                  </div>
                </div>
              )}

              {servicesSubmitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center text-red-800">
                    <i className="ri-error-warning-line mr-2"></i>
                    Une erreur s'est produite. Veuillez réessayer.
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowServicesModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isServicesSubmitting}
                  className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isServicesSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Envoi...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Proposer mes services
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default QuickHelpSection;
